<template>
<u-plot-stacked
    ref="plot"
    v-if="plotData.length"
    :data="plotData"
    :series="plotSeries"
    :dark="$vuetify.theme.dark"
    duration
/>
</template>

<script>
import iso8601Duration from '@/services/iso8601Duration.js';
import uPlotStacked from '@/components/uPlotStacked.vue';

export default {
    props: {
        value: {type: Object, required: false},
        attrs: {type: Object, required: true}
    },
    components: {uPlotStacked},
    computed: {
        formatLabel () { return (group, key) => this.$style.label.short(key); },
        plotData () {
            const rtn = [[], []];
            if (this.attrs.humidity) rtn.push([]);
            else if (this.attrs.vibration) rtn.push([]);
            rtn.push([]); // step number
            rtn.push([]); // counter a
            if (this.attrs.model !== 'es102')
                rtn.push([]); // counter b
            if (this.attrs.time_signals && this.attrs.time_signals.length)
                this.attrs.time_signals.forEach(() => rtn.push([]));
            
            let ptemp = this.value.temp && this.value.temp.mode === 'SV' ? this.value.temp.set_value : 0;
            let pvib = 0;
            let phumi = this.attrs.humidity && this.value.humi && this.value.humi.mode === 'SV' ? this.value.humi.set_value : 0;
            let time = 0;

            const pushPoint = (s, n) => {
                let idx = 0
                rtn[idx++].push(time);
                rtn[idx++].push(ptemp);
                if (this.attrs.humidity)
                    rtn[idx++].push(phumi);
                else if (this.attrs.vibration)
                    rtn[idx++].push(pvib);
                rtn[idx++].push(n); //step
                (this.attrs.model !== 'es102' ? [this.value.counter_a, this.value.counter_b] : [this.value.counter_a])
                .forEach(c => {
                    rtn[idx++].push(n >= c.start && n <= c.end && c.cycles ? `x ${c.cycles}` : '');
                });
                if (this.attrs.time_signals && this.attrs.time_signals.length)
                    this.attrs.time_signals.forEach(t => rtn[idx++].push(s[t.key]));
            }

            this.value.steps.forEach((s, i) => {
                const snum = i + 1
                pushPoint(s, snum);
                ptemp = s.temp.ramp ? ptemp : s.temp.set_value;
                if (this.attrs.humidity)
                    phumi = s.humi.ramp ? phumi : (s.humi.enable ? s.humi.set_value : null);
                if (this.attrs.vibration)
                    pvib = s.vib.ramp ? pvib : (s.vib.enable ? s.vib.set_value : null);
                pushPoint(s, snum);
                time += iso8601Duration.toSeconds(s.duration);
                ptemp = s.temp.set_value;
                phumi = this.attrs.humidity ? (s.humi.enable ? s.humi.set_value : null) : phumi;
                pvib = this.attrs.vibration ? (s.vib.enable ? s.vib.set_value : null) : pvib;
                pushPoint(s, snum);
            });

            return rtn;
        },
        plotSeries () {
            const lcolors = {temp: "#7f0000", vib: "#800CBD", humi: "#403DCE"};
            const tsig_text = v => v ? this.$t('On') : this.$t('Off');
            const rtn = [
                {
                    "col": "temp__set_value", 
                    "ptype": "num",
                    "label": this.$style.label.short('temp__set_value'),
                    "stroke": lcolors.temp,
                    "width": 2,
                    "show": true,
                    "units": this.attrs.units.temp,
                    points: {show: false}
                }
            ];
            if (this.attrs.humidity) {
                rtn.push({
                    "col": "humi__set_value",
                    "spanGaps": false,
                    "ptype": "num",
                    "label": this.$style.label.short('humi__set_value'),
                    "stroke": lcolors.humi,
                    "width": 2,
                    "show": true,
                    "units": this.attrs.units.humi,
                    points: {show: false}
                });
            }
            else if (this.attrs.vibration) {
                rtn.push({
                    "col": "vib__set_value",
                    "spanGaps": false,
                    "ptype": "num",
                    "label": this.$style.label.short('vib__set_value'),
                    "stroke": lcolors.vib,
                    "width": 2,
                    "show": true,
                    "units": this.attrs.units.vib,
                    points: {show: false}
                });
            }
            rtn.push(...[
                {
                    "col": "step",
                    "ptype": "state",
                    "label": this.$t("Step"),
                    "stroke": {},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                },
                {
                    "col": "step",
                    "ptype": "state",
                    "label": this.$t("Counter A"),
                    "stroke": {'': 'black'},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                },
            ]);
            if (this.attrs.model !== 'es102') {
                rtn.push({
                    "col": "step",
                    "ptype": "state",
                    "label": this.$t("Counter B"),
                    "stroke": {'': 'black'},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                });
            }
            if (this.attrs.time_signals && this.attrs.time_signals.length) {
                this.attrs.time_signals.forEach(t => {
                    rtn.push({
                        "col": t.key,
                        "ptype": "bool",
                        "label": this.formatLabel('output', t.key),
                        "stroke": {'false': 'black'},
                        "points": false,
                        "width": 1,
                        "show": true,
                        "dash": [],
                        i18n: tsig_text
                    });
                });
            }
            return rtn;
        }
    },
    watch: {
        value: {
            deep: true,
            handler () { if (this.$refs.plot) this.$refs.plot.redraw(this.plotData); }
        }
    }
}
</script>